import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";
import {
  useOptimizedOrders,
  useAiMetricsOverview,
  useCustomerSatisfaction,
  useAiProductsMetrics,
  useAiAgentsMetrics,
  useAiPerformanceMetrics,
} from "../../hooks/useDashboardQueries";
import { useGlobalUtils } from "../../lib/globalUtils";
import { whatsappConversationTotals } from "../../lib/whatsappConversationMetric";
import {
  LoadingState,
  StatsGrid,
  PopularItems,
  OrderRow,
  AiAgentsDistributionChart,
  AiPerformanceCards,
} from "../ui";
import { BusinessDropdown } from "../ui/Business/BusinessDropdown";
import TrialExtensionCard from "../ui/Billing/TrialExtensionCard";
import { Icon } from "../ui/UIElements/Icon";
import { Button } from "../ui/UIElements/Button";
import MonthYearPicker from "./reports/MonthYearPicker";
import { getCurrentMonth } from "../../lib/reportUtils";

import { useOverviewStates } from "../../hooks/useOverviewStates";
import { buildWhereClause } from "@finitless/shared/orderConstants";
import { buildSatisfactionWhereClause } from "../../constants/reportsConstants";
import "./overview-reports.css";

const OverviewPage = () => {
  const { t } = useTranslation("overview");
  const { parsePrice } = useGlobalUtils();
  const navigate = useNavigate();
  const welcomeMessage = t("welcomeMessage");

  // Local state for business filter (doesn't modify global context)
  const [filterBusiness, setFilterBusiness] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isBusinessDropdownOpen, setIsBusinessDropdownOpen] = useState(false);
  const businessDropdownRef = useRef(null);

  // Close business dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        businessDropdownRef.current &&
        !businessDropdownRef.current.contains(event.target)
      ) {
        setIsBusinessDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleBusinessSelect = (brand) => {
    setFilterBusiness(brand);
    setIsBusinessDropdownOpen(false);
  };

  const handleClearBusinessFilter = () => {
    setFilterBusiness(null);
  };

  // Helper function to convert Date to YYYY-MM format
  const getMonthString = (date) => {
    if (!date) return getCurrentMonth();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  const handleViewAllOrders = () => {
    navigate("/orders");
  };

  const handleOrderClick = (order) => {
    navigate(`/orders/${order.id}`, { state: { order } });
  };

  // Build query params with filters
  const queryParams = useMemo(() => {
    const params = {};
    if (filterBusiness?.id) {
      params.business_id = filterBusiness.id;
    }
    if (selectedDate) {
      params.month = getMonthString(selectedDate);
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) {
        params.timezone = tz;
      } else {
        params.timezone_offset = -(new Date().getTimezoneOffset() / 60);
      }
    }
    return params;
  }, [filterBusiness?.id, selectedDate]);

  // Use filterBusiness if set, otherwise don't filter by business (null)
  const businessForFilter = filterBusiness || null;

  // Data hooks - only keeping what's needed for sections not replaced by AI metrics
  const recentOrdersData = useOptimizedOrders({
    mode: "dashboard",
    orderBy: "-id",
    page: 1,
    page_size: 5,
    where: buildWhereClause("all", "", businessForFilter),
  });
  const customerSatisfactionData = useCustomerSatisfaction({
    where: buildSatisfactionWhereClause(businessForFilter),
  });
  const aiMetricsData = useAiMetricsOverview(queryParams);
  const aiProductsData = useAiProductsMetrics(queryParams);
  const aiAgentsData = useAiAgentsMetrics(queryParams);
  const aiPerformanceData = useAiPerformanceMetrics(queryParams);

  const { isLoading, individualStates } = useOverviewStates({
    recentOrdersData,
    customerSatisfactionData,
    aiMetricsData,
    aiProductsData,
    aiAgentsData,
    aiPerformanceData,
  });

  const whatsappSessions = whatsappConversationTotals(
    aiMetricsData.data?.conversations?.by_provider_type,
  );

  // Helper function to format change percentage
  const formatChangePercentage = (change) => {
    if (change === null || change === undefined) return null;
    const sign = change >= 0 ? "+" : "";
    return `${sign}${change.toFixed(1)}%`;
  };

  // AI Metrics Cards based on the API response structure
  const metricCards = [
    {
      id: "ai-orders",
      title: t("metrics.todaysOrders"),
      titleHelp: {
        ariaLabel: t("help.metrics.todaysOrders.ariaLabel"),
        whatIs: t("help.metrics.todaysOrders.whatIs"),
        period: t("help.metrics.todaysOrders.period"),
      },
      value: aiMetricsData.data?.orders?.current ?? 0,
      change: formatChangePercentage(
        aiMetricsData.data?.orders?.change_percentage,
      ),
      yesterday: aiMetricsData.data?.orders?.previous ?? 0,
      icon: "ShoppingCart",
      iconColor: "text-success",
      iconBg: "bg-success-bg",
      gradient: false,
    },
    {
      id: "ai-revenue",
      title: t("metrics.todaysRevenue"),
      titleHelp: {
        ariaLabel: t("help.metrics.todaysRevenue.ariaLabel"),
        whatIs: t("help.metrics.todaysRevenue.whatIs"),
        period: t("help.metrics.todaysRevenue.period"),
      },
      value: parsePrice(aiMetricsData.data?.revenue?.total?.current ?? 0),
      change: formatChangePercentage(
        aiMetricsData.data?.revenue?.total?.change_percentage,
      ),
      yesterday: parsePrice(aiMetricsData.data?.revenue?.total?.previous ?? 0),
      icon: "DollarSign",
      iconColor: "text-primary",
      iconBg: "bg-accent",
    },
    {
      id: "ai-avg-order-value",
      title: t("metrics.avgOrderValue"),
      titleHelp: {
        ariaLabel: t("help.metrics.avgOrderValue.ariaLabel"),
        whatIs: t("help.metrics.avgOrderValue.whatIs"),
        formula: t("help.metrics.avgOrderValue.formula"),
        period: t("help.metrics.avgOrderValue.period"),
      },
      value: parsePrice(aiMetricsData.data?.revenue?.average?.current ?? 0),
      change: formatChangePercentage(
        aiMetricsData.data?.revenue?.average?.change_percentage,
      ),
      yesterday: parsePrice(
        aiMetricsData.data?.revenue?.average?.previous ?? 0,
      ),
      icon: "BarChart3",
      iconColor: "text-primary",
      iconBg: "bg-accent",
    },
    {
      id: "ai-conversations",
      title: t("metrics.totalConversations"),
      titleHelp: {
        ariaLabel: t("help.metrics.totalConversations.ariaLabel"),
        whatIs: t("help.metrics.totalConversations.whatIs"),
        period: t("help.metrics.totalConversations.period"),
      },
      value: aiMetricsData.data?.conversations?.total?.current ?? 0,
      change: formatChangePercentage(
        aiMetricsData.data?.conversations?.total?.change_percentage,
      ),
      yesterday: aiMetricsData.data?.conversations?.total?.previous ?? 0,
      icon: "Bot",
      iconColor: "text-warning",
      iconBg: "bg-warning-bg",
    },
    {
      id: "ai-chat-sessions",
      title: t("metrics.chatSessions"),
      titleHelp: {
        ariaLabel: t("help.metrics.chatSessions.ariaLabel"),
        whatIs: t("help.metrics.chatSessions.whatIs"),
        period: t("help.metrics.chatSessions.period"),
      },
      value: whatsappSessions.current,
      change: formatChangePercentage(whatsappSessions.changePercentage),
      yesterday: whatsappSessions.previous,
      icon: "MessageCircle",
      iconColor: "text-info",
      iconBg: "bg-accent",
    },
  ];

  if (isLoading) {
    return (
      <LoadingState
        selectedBrand={filterBusiness}
        welcomeMessage={welcomeMessage}
        cardCount={5}
      />
    );
  }

  return (
    <div className="ordering-analytics-page ordering-overview-page">
      <PageHeader
        title={
          filterBusiness
            ? t("header.titleWithBusiness", { name: filterBusiness.name })
            : t("header.title")
        }
        subtitle={welcomeMessage}
      />

      <TrialExtensionCard className="mb-6" />

      {/* Filters */}
      <div className="ordering-analytics-page__filter-panel border-border bg-background mb-6 rounded-lg border p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Left side - Business Filter */}
          <div className="flex flex-1 flex-col gap-4 sm:flex-row">
            {/* Business Filter Dropdown */}
            <div className="relative flex-shrink-0" ref={businessDropdownRef}>
              <Button
                variant="ghost"
                type="button"
                onClick={() =>
                  setIsBusinessDropdownOpen(!isBusinessDropdownOpen)
                }
                aria-expanded={isBusinessDropdownOpen}
                aria-controls="overview-business-dropdown"
                className="ordering-analytics-page__control hover:bg-accent border-border bg-card flex h-10 w-full min-w-[180px] items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors sm:w-auto"
              >
                <span className="text-foreground truncate">
                  {filterBusiness
                    ? filterBusiness.name
                    : t("filters.allBusinesses")}
                </span>
                <Icon
                  name={isBusinessDropdownOpen ? "ChevronUp" : "ChevronDown"}
                  className="text-muted-foreground ml-auto h-4 w-4 flex-shrink-0"
                />
              </Button>
              {filterBusiness && (
                <Button
                  variant="ghost"
                  type="button"
                  onClick={handleClearBusinessFilter}
                  aria-label={t("filters.clearBusiness")}
                  className="ordering-analytics-page__clear bg-error hover:bg-error/90 absolute -top-2 -right-2 z-10 flex h-5 w-5 items-center justify-center rounded-full transition-colors"
                  title={t("filters.clearBusiness")}
                >
                  <Icon
                    name="X"
                    className="h-3 w-3 text-[var(--error-foreground)]"
                  />
                </Button>
              )}
              <div
                id="overview-business-dropdown"
                className="absolute top-full left-0 z-[var(--layer-popover)] mt-2"
              >
                <BusinessDropdown
                  isOpen={isBusinessDropdownOpen}
                  onClose={() => setIsBusinessDropdownOpen(false)}
                  selectedBrand={filterBusiness}
                  onBrandSelect={handleBusinessSelect}
                />
              </div>
            </div>

            {/* Period Filter */}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="ghost"
                type="button"
                onClick={() => setSelectedDate(null)}
                aria-pressed={selectedDate === null}
                className={`ordering-analytics-page__period h-10 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedDate === null
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-accent border-border bg-card text-foreground border"
                }`}
              >
                {t("date.today", { ns: "common" })}
              </Button>
              <Button
                variant="ghost"
                type="button"
                onClick={() => {
                  const now = new Date();
                  setSelectedDate(
                    new Date(now.getFullYear(), now.getMonth(), 1),
                  );
                }}
                aria-pressed={
                  Boolean(selectedDate) &&
                  selectedDate.getMonth() === new Date().getMonth() &&
                  selectedDate.getFullYear() === new Date().getFullYear()
                }
                className={`ordering-analytics-page__period h-10 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedDate &&
                  selectedDate.getMonth() === new Date().getMonth() &&
                  selectedDate.getFullYear() === new Date().getFullYear()
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-accent border-border bg-card text-foreground border"
                }`}
              >
                {t("date.currentMonth", { ns: "common" })}
              </Button>
              <Button
                variant="ghost"
                type="button"
                onClick={() => {
                  const now = new Date();
                  setSelectedDate(
                    new Date(now.getFullYear(), now.getMonth() - 1, 1),
                  );
                }}
                aria-pressed={
                  Boolean(selectedDate) &&
                  selectedDate.getMonth() ===
                    new Date(
                      new Date().getFullYear(),
                      new Date().getMonth() - 1,
                      1,
                    ).getMonth() &&
                  selectedDate.getFullYear() ===
                    new Date(
                      new Date().getFullYear(),
                      new Date().getMonth() - 1,
                      1,
                    ).getFullYear()
                }
                className={`ordering-analytics-page__period h-10 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedDate &&
                  selectedDate.getMonth() ===
                    new Date(
                      new Date().getFullYear(),
                      new Date().getMonth() - 1,
                      1,
                    ).getMonth() &&
                  selectedDate.getFullYear() ===
                    new Date(
                      new Date().getFullYear(),
                      new Date().getMonth() - 1,
                      1,
                    ).getFullYear()
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-accent border-border bg-card text-foreground border"
                }`}
              >
                {t("date.lastMonth", { ns: "common" })}
              </Button>
              <MonthYearPicker
                value={selectedDate}
                onChange={setSelectedDate}
                placeholder={t("date.selectMonth", { ns: "common" })}
                id="overview-month-picker"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      {individualStates.aiMetrics.isLoading ? (
        <div className="mb-4 grid grid-cols-1 gap-4 sm:mb-6 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card animate-pulse p-4 sm:p-6">
              <div className="bg-muted mb-3 h-3 w-1/2 rounded sm:mb-4 sm:h-4"></div>
              <div className="bg-muted h-6 w-3/4 rounded sm:h-8"></div>
            </div>
          ))}
        </div>
      ) : individualStates.aiMetrics.error ? (
        <div className="card border-error/40 bg-error-bg mb-4 p-4 sm:mb-6 sm:p-6">
          <p className="text-error text-sm">{t("errors.metrics")}</p>
        </div>
      ) : (
        <StatsGrid stats={metricCards} columns={3} className="mb-4 sm:mb-6" />
      )}

      {/* AI Performance Dashboard */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:mt-6 sm:gap-6">
        <AiPerformanceCards
          data={individualStates.aiPerformance.data}
          isLoading={individualStates.aiPerformance.isLoading}
          error={individualStates.aiPerformance.error}
        />
      </div>

      {/* Popular Items - AI Products */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:mt-6 sm:gap-6">
        {individualStates.aiProducts.isLoading ? (
          <div className="card animate-pulse p-4 sm:p-6">
            <div className="bg-muted mb-4 h-5 w-1/3 rounded sm:mb-6 sm:h-6"></div>
            <div className="space-y-3 sm:space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="bg-muted h-12 rounded sm:h-16"></div>
              ))}
            </div>
          </div>
        ) : individualStates.aiProducts.error ? (
          <div className="card border-warning/40 bg-warning-bg p-4 sm:p-6">
            <h2 className="text-foreground mb-2 text-base font-semibold sm:text-lg">
              {t("popularItems.title")}
            </h2>
            <p className="text-warning text-sm">{t("errors.popularItems")}</p>
          </div>
        ) : (
          <PopularItems
            title={t("popularItems.title")}
            items={
              individualStates.aiProducts.data?.top_products?.map(
                (product) => ({
                  id: product.product_id,
                  name: product.name,
                  sales: product.times_sold,
                  revenue: product.revenue,
                  orders: product.orders,
                }),
              ) || []
            }
            itemLabel={t("popularItems.itemLabel")}
            emptyMessage={t("popularItems.empty")}
          />
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:mt-6 sm:gap-6 lg:grid-cols-2">
        {/* Recent Orders */}
        {individualStates.recentOrders.isLoading ? (
          <div className="card animate-pulse p-2 sm:p-6">
            <div className="border-border border-b p-2 sm:p-4">
              <div className="bg-muted h-5 w-1/3 rounded sm:h-6"></div>
            </div>
            <div className="divide-border divide-y">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="p-2 sm:p-4">
                  <div className="bg-muted h-12 rounded sm:h-16"></div>
                </div>
              ))}
            </div>
          </div>
        ) : individualStates.recentOrders.error ? (
          <div className="card p-2 sm:p-6">
            <div className="border-border border-b p-2 sm:p-4">
              <h2 className="text-foreground text-base font-semibold sm:text-lg">
                {t("recentOrders.title")}
              </h2>
            </div>
            <div className="border-warning/40 bg-warning-bg border-t p-2 sm:p-4">
              <p className="text-warning text-sm">{t("errors.recentOrders")}</p>
            </div>
          </div>
        ) : (
          <div className="card p-2 sm:p-2">
            <div className="border-border border-b p-2 sm:p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-foreground text-base font-semibold sm:text-lg">
                  {t("recentOrders.title")}
                </h2>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={handleViewAllOrders}
                  className="ordering-analytics-page__link text-primary hover:text-primary/90 cursor-pointer text-xs font-medium transition-colors duration-200 sm:text-sm"
                >
                  {t("recentOrders.viewAll")}
                </Button>
              </div>
            </div>
            <div className="divide-border divide-y">
              {individualStates.recentOrders.data?.orders &&
              individualStates.recentOrders.data.orders.length > 0 ? (
                individualStates.recentOrders.data.orders
                  .slice(0, 5)
                  .map((order, index) => (
                    <OrderRow
                      key={order.id || index}
                      order={order}
                      index={index}
                      onClick={handleOrderClick}
                    />
                  ))
              ) : (
                <div className="text-muted-foreground p-3 text-center text-sm sm:p-4">
                  {t("recentOrders.empty")}
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI Agents Distribution Chart */}
        <AiAgentsDistributionChart
          data={individualStates.aiAgents.data}
          isLoading={individualStates.aiAgents.isLoading}
          error={individualStates.aiAgents.error}
        />

        {/* Customer Satisfaction */}
        {/* {individualStates.customerSatisfaction.isLoading ? (
          <div className="card p-6 animate-pulse">
            <div className="h-6 bg-muted rounded w-1/3 mb-6"></div>
            <div className="bg-card/50 rounded-lg p-6">
              <div className="h-32 bg-muted rounded"></div>
            </div>
          </div>
        ) : individualStates.customerSatisfaction.error ? (
          <div className="card p-6 border-warning/40 bg-warning-bg">
            <h2 className="text-lg font-semibold text-foreground mb-2">Customer Satisfaction</h2>
            <p className="text-warning text-sm">Unable to load satisfaction data.</p>
          </div>
        ) : (
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-foreground mb-6">Customer Satisfaction</h2>

            <div className="grid grid-cols-1 gap-4 mb-6">
              <div className="bg-card/50 rounded-lg p-6">
                <CustomerSatisfaction
                  data={individualStates.customerSatisfaction.data?.satisfaction}
                />
              </div>
            </div>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default OverviewPage;
